// Thin fetch wrapper. Injects CSRF on mutating calls, bubbles 401 for auth redirect.

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown, message: string) {
    super(message);
  }
}

const SAFE = new Set(["GET", "HEAD", "OPTIONS"]);

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

type Init = Omit<RequestInit, "body"> & { body?: unknown };

export async function api<T>(path: string, init: Init = {}): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  const headers = new Headers(init.headers || {});
  headers.set("Accept", "application/json");
  const body = init.body;
  let serialized: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    headers.set("Content-Type", "application/json");
    serialized = JSON.stringify(body);
  }
  if (!SAFE.has(method) && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }
  const resp = await fetch(path, {
    ...init,
    method,
    headers,
    body: serialized,
    credentials: "same-origin",
  });
  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  let payload: unknown = text;
  if (text && resp.headers.get("content-type")?.includes("application/json")) {
    try {
      payload = JSON.parse(text);
    } catch {
      /* leave raw */
    }
  }
  if (!resp.ok) {
    const detail =
      typeof payload === "object" && payload !== null && "detail" in payload
        ? (payload as { detail: unknown }).detail
        : payload;
    throw new ApiError(resp.status, detail, `${method} ${path} -> ${resp.status}`);
  }
  return payload as T;
}
